import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Info } from "@phosphor-icons/react";

/**
 * Reusable field-level Help icon with tooltip popover.
 * Place inline next to a label or input.
 *
 * Props:
 *  - moduleKey: string (required)
 *  - fieldKey: string (required)
 *  - fallback: optional string to show if no content found
 */
const HelpFieldIcon = ({ moduleKey, fieldKey, fallback = "" }) => {
  const [show, setShow] = useState(false);
  const [entry, setEntry] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!show || fetchedRef.current) return;
    fetchedRef.current = true;
    axios
      .get(`${API}/help-content?module=${encodeURIComponent(moduleKey)}&field_key=${encodeURIComponent(fieldKey)}`)
      .then((r) => {
        setEntry((r.data || [])[0] || null);
      })
      .catch(() => setEntry(null));
  }, [show, moduleKey, fieldKey]);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        data-testid={`help-field-${moduleKey}-${fieldKey}`}
        onClick={() => setShow((s) => !s)}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="inline-flex items-center justify-center w-4 h-4 ml-1 text-slate-400 hover:text-blue-700"
      >
        <Info size={12} weight="bold" />
      </button>
      {show && (
        <span
          data-testid={`help-popover-${moduleKey}-${fieldKey}`}
          role="tooltip"
          className="absolute left-0 top-full mt-1 z-[200] w-64 bg-slate-900 text-white text-xs leading-relaxed p-3 shadow-2xl"
        >
          <span className="block font-semibold text-blue-200 mb-1">{entry?.title || fieldKey}</span>
          <span className="block opacity-90">{entry?.purpose || fallback || "No help available yet."}</span>
          {entry?.important && (
            <span className="block mt-1.5 pt-1.5 border-t border-white/20 text-amber-200">
              <strong className="uppercase tracking-wider text-[9px]">Note:</strong> {entry.important}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default HelpFieldIcon;
